<script setup>
// "When & where" — the schedule and playing area of a match in their own
// card. One canon for the fixture page and the ladder challenge page: the
// challenge carries the same fixture once accepted, so it shows the same
// section. Renders nothing when there is neither a date nor an area (meet
// fixtures always render: the card carries the note that replaces every
// hidden schedule-editing control).
import {computed} from 'vue';
import {ladderFixtureSchedule, ladderFixtureViewer} from '~/utils/challengeSchedule';
import {useAuthService} from '~/composables/useAuthService.js';
import {useValidation} from '~/composables/useValidation.js';

const props = defineProps({
  fixture: {type: Object, default: null},
  headClass: {type: String, default: ''},
  cardClass: {type: String, default: ''},
});

const {outputDateFromDb} = useCrudConverters();

const isMeetFixture = computed(() => props.fixture?.meetOccurrenceId != null);

// A ladder match reads like its challenge page (utils/challengeSchedule.js):
// who suggested, who confirms, agreed by both / set by a club admin.
const {user, isAuthenticated, isAdminOfClub} = useAuthService();
const {myTeamFromListOfTeams} = useValidation();
const ladder = computed(() => {
  const f = props.fixture;
  if (!f?.ladderChallengeId) return null;
  const myTeam = isAuthenticated.value && user.value
      ? myTeamFromListOfTeams(user.value.playerId, [f.homeTeam, f.awayTeam].filter(Boolean)) : null;
  const clubAdmin = isAuthenticated.value && f.clubId != null && isAdminOfClub(f.clubId);
  const s = ladderFixtureSchedule(f, ladderFixtureViewer(f, myTeam?.id ?? null, clubAdmin));
  return s && ['S4', 'S4p', 'S5', 'S6'].includes(s.state) ? s : null;
});

const whenAndWhere = computed(() => {
  const f = props.fixture;
  if (!f) return null;
  const when = f.scheduledAt || f.homeTeamScheduledAt || f.awayTeamScheduledAt;
  if (!when && !f.playingAreaName) return null;
  return {
    when,
    confirmed: f.scheduledAt != null,
    area: f.playingAreaName || null,
  };
});

const show = computed(() => props.fixture != null && (whenAndWhere.value != null || isMeetFixture.value));
</script>

<template>
  <template v-if="show">
    <div class="tl-section-head" :class="headClass"><h2>When &amp; where</h2></div>
    <div class="tl-card" :class="cardClass">
      <div v-if="whenAndWhere?.when" class="fww-row">
        <div class="fww-icon">
          <v-icon size="17" color="charcoal">mdi-calendar-clock</v-icon>
        </div>
        <div class="flex-grow-1">
          <div class="fww-title">{{ outputDateFromDb(whenAndWhere.when, 'cccc d MMMM yyyy, HH:mm') }}</div>
          <div class="fww-sub" v-if="ladder">{{ ladder.line }}</div>
          <div class="fww-sub" v-else>{{ isMeetFixture ? 'Set by the meet' : whenAndWhere.confirmed ? 'Confirmed by both sides' : 'Proposed, awaiting confirmation' }}</div>
        </div>
        <v-chip v-if="ladder" variant="flat" size="small" class="fww-chip"
                :class="['tl-chip', `tl-chip--${ladder.chip.kind === 'muted' ? 'neutral' : ladder.chip.kind}`]">
          {{ ladder.chip.label }}
        </v-chip>
        <v-chip v-else variant="flat" size="small" class="fww-chip"
                :class="['tl-chip', whenAndWhere.confirmed ? 'tl-chip--ok' : 'tl-chip--warn']">
          {{ whenAndWhere.confirmed ? 'Confirmed' : 'Proposed' }}
        </v-chip>
      </div>
      <div v-if="whenAndWhere?.when && whenAndWhere?.area" class="fww-divider"></div>
      <div v-if="whenAndWhere?.area" class="fww-row">
        <div class="fww-icon">
          <v-icon size="17" color="charcoal">mdi-map-marker-outline</v-icon>
        </div>
        <div class="flex-grow-1">
          <div class="fww-title">{{ whenAndWhere.area }}</div>
          <div class="fww-sub">Playing area</div>
        </div>
      </div>
      <div v-if="isMeetFixture && (whenAndWhere?.when || whenAndWhere?.area)" class="fww-divider"></div>
      <div v-if="isMeetFixture" class="fww-row">
        <div class="fww-icon">
          <v-icon size="17" color="charcoal">mdi-handshake-outline</v-icon>
        </div>
        <div class="flex-grow-1">
          <div class="fww-sub">This match follows its meet date, so change the date on the meet instead.</div>
        </div>
      </div>
    </div>
  </template>
</template>

<style scoped>
/* Same hairline card rhythm as the fixture page's points snapshot */
.fww-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
}

.fww-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: rgb(var(--v-theme-light));
  border: 1px solid rgb(var(--v-theme-borderLight));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.fww-title {
  font-size: 13.5px;
  font-weight: 600;
  color: rgb(var(--v-theme-text));
}

.fww-sub {
  font-size: 11.5px;
  color: rgb(var(--v-theme-textSubtle));
}

.fww-chip {
  flex-shrink: 0;
}

.fww-divider {
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}
</style>
